
import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { Eye } from "lucide-react";
import Button from "../../../../../../common/element/Buttons";
import ToolManagmentCard from "../../../../../../common/element/Card/ToolManagmnetCard";
import AddEditQuestionAnswer from "./EditQuestionAnswer";
import { getScienceOfSelfSessionDetails } from "../../../../../../redux/ScienceOfSelf/Scienceofselfslice";
import {
  selectScienceOfSelfSessionDetails,
  selectScienceOfSelfSessionDetailsLoading,
} from "../../../../../../redux/ScienceOfSelf/Scienceofselfselectors";
import ThreeDotBouncingLoader from "../../../../../../common/element/ButtonLoader/ThreeDotBouncingLoder";

// Helper: format tool name (e.g. "inner-needs" -> "Inner Needs")
const formatToolName = (name) => {
  if (!name) return "";
  return name
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Helper: capitalize first letter
const capitalizeFirstLetter = (str) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

// Helper function for dynamic status colors
const getStatusBadge = (status) => {
  switch (status?.toLowerCase()) {
    case "completed":
      return "text-green-600 bg-green-50";
    case "upcoming":
      return "text-blue-600 bg-blue-50";
    case "cancelled":
      return "text-red-600 bg-red-50";
    case "ongoing":
      return "text-[#CA8A04] bg-[#FEF9C3]";
    default:
      return "text-gray-600 bg-gray-50";
  }
};

// Helper: Extract filename from URL
const getFileNameFromUrl = (url) => {
  if (!url) return "Document";
  return url.substring(url.lastIndexOf("/") + 1);
};

// ─── Sub-component: renders a single tool's result based on tool_id ────────
const ToolResultSection = ({ tool }) => {
  const toolId = tool.tool_id;
  const score = tool.result_json;
  const isCompleted = tool.status === "completed";

  const isNCodesResult = toolId === 1 || toolId === 2 || toolId === 3;
  const isInnerDriveResult = toolId === 4;
  const isHumanNeedsResult = toolId === 8;
  const isBaselineResult = toolId === 5;
  const isPersonalityTestResult = toolId === 6;
  const isTraitsTestResult = toolId === 7;

  // Inner Drive rows – top 2 as per assessment (Tool 4)
  const innerDriveAssessmentRows = useMemo(() => {
    if (isInnerDriveResult && score?.user_ordered_items?.top_assessment) {
      return score.user_ordered_items.top_assessment.map((name) => ({
        name,
        score: score[name] ?? 0,
      }));
    }
    return [];
  }, [isInnerDriveResult, score]);

  // Inner Drive rows – top 2 after coach discussion (Tool 4)
  const innerDriveCoachRows = useMemo(() => {
    if (isInnerDriveResult && score?.user_ordered_items?.top_after_coach) {
      return score.user_ordered_items.top_after_coach.map((name) => ({
        name,
        score: score[name] ?? 0,
      }));
    }
    return [];
  }, [isInnerDriveResult, score]);

  // Inner Drive fallback rows when user_ordered_items is absent (Tool 4)
  const innerDriveFallbackRows = useMemo(() => {
    if (isInnerDriveResult && score) {
      return [
        { key: "Certainty", value: score.Certainty || 0 },
        { key: "Variety", value: score.Variety || 0 },
        { key: "Significance", value: score.Significance || 0 },
        { key: "Love/Connection", value: score["Love/Connection"] || 0 },
        { key: "Growth", value: score.Growth || 0 },
        { key: "Contribution", value: score.Contribution || 0 },
      ].sort((a, b) => b.value - a.value);
    }
    return [];
  }, [isInnerDriveResult, score]);

  // Human Needs rows (Tool 8)
  const humanNeedsRows = useMemo(() => {
    if (isHumanNeedsResult && score) {
      return [
        { key: "Certainty", value: score.Certainty || 0 },
        { key: "Variety", value: score.Variety || 0 },
        { key: "Significance", value: score.Significance || 0 },
        { key: "Love/Connection", value: score["Love/Connection"] || 0 },
        { key: "Growth", value: score.Growth || 0 },
        { key: "Contribution", value: score.Contribution || 0 },
      ].sort((a, b) => b.value - a.value);
    }
    return [];
  }, [isHumanNeedsResult, score]);

  // Personality Test rows (Tool 6)
  const personalityTestRows = useMemo(() => {
    if (isPersonalityTestResult && score) {
      return Object.entries(score)
        .filter(([key]) => key !== 'user_ordered_items')
        .map(([key, value]) => ({ key, value }))
        .sort((a, b) => b.value - a.value);
    }
    return [];
  }, [isPersonalityTestResult, score]);

  const personalityAssessmentType = useMemo(() => {
    if (isPersonalityTestResult && score?.user_ordered_items?.assessment_type) {
      return score.user_ordered_items.assessment_type.map((name) => ({
        name,
        score: score[name] ?? 0,
      }));
    }
    return [];
  }, [isPersonalityTestResult, score]);

  const personalityCoachType = useMemo(() => {
    if (isPersonalityTestResult && score?.user_ordered_items?.coach_type) {
      return score.user_ordered_items.coach_type.map((name) => ({
        name,
        score: score[name] ?? 0,
      }));
    }
    return [];
  }, [isPersonalityTestResult, score]);

  const highestPersonalityScore =
    personalityTestRows.length > 0 ? personalityTestRows[0]?.value : 0;

  // Traits Test rows (Tool 7)
  const traitsTestRows = useMemo(() => {
    if (isTraitsTestResult && score) {
      const traits = [];
      const traitNames = Object.keys(score)
        .filter((key) => key.endsWith("Level"))
        .map((key) => key.replace("Level", ""));

      traitNames.forEach((trait) => {
        const levelKey = `${trait}Level`;
        const scoreKey = `${trait}Score`;
        traits.push({
          name: trait.charAt(0).toUpperCase() + trait.slice(1),
          level: score[levelKey] || "",
          score: score[scoreKey] || 0,
        });
      });
      return traits.sort((a, b) => b.score - a.score);
    }
    return [];
  }, [isTraitsTestResult, score]);

  // ── Not completed yet ──
  if (!isCompleted) {
    return (
      <div>
        <h3 className="font-lato font-semibold text-[16px] leading-[150%] text-[#101828] mb-3 text-start">
          {formatToolName(tool.tool_name)}
        </h3>
        <p className="font-lato font-normal text-[14px] leading-[150%] text-center text-[#475467] mb-2">
          Not Completed Yet.
        </p>
      </div>
    );
  }

  // ── Completed – render table per tool_id ──
  return (
    <div className="bg-white overflow-hidden">
      {/* Tool Name Header */}
      {tool.tool_name && (
        <h3 className="font-lato font-semibold text-[16px] leading-[150%] text-[#101828] mb-3 text-start">
          {formatToolName(tool.tool_name)}
        </h3>
      )}

      {/* TOOL ID 1, 2, 3: N-Codes / Inner Drives Assessment */}
      {isNCodesResult && score?.selected_codes && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead>
              <tr className="bg-[#FFF4E5]">
                <th className="py-3 px-4 font-lato font-medium text-sm text-[#475467] rounded-l-md">
                  N-Code
                </th>
                <th className="py-3 px-4 font-lato font-medium text-sm text-[#475467] rounded-r-md">
                  E-Code
                </th>
              </tr>
            </thead>
            <tbody className="bg-white text-start">
              {score.selected_codes.map((item, index) => (
                <tr
                  key={item.id || index}
                  className="border-t border-gray-200"
                >
                  <td className="py-3 px-4 font-lato font-medium text-sm text-[#1D2939]">
                    {item.ncode}
                  </td>
                  <td className="py-3 px-4 font-lato font-medium text-sm text-[#1D2939]">
                    {item.ecode}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* TOOL ID 4: Inner Drive Assessment */}
      {isInnerDriveResult && (
        innerDriveAssessmentRows.length > 0 ? (
          <div className="space-y-6">
            <div>
              <h4 className="font-lato font-semibold text-[14px] leading-[150%] text-[#101828] mb-3 text-start">
                Top 2 Inner Drives – As Per Assessment:
              </h4>
              <table className="w-full border border-gray-200 rounded-lg overflow-hidden">
                <thead>
                  <tr className="bg-[#FFF4E5]">
                    <th className="py-3 px-4 text-left font-lato font-medium text-sm text-[#475467]">Rank</th>
                    <th className="py-3 px-4 text-left font-lato font-medium text-sm text-[#475467]">Inner Drive</th>
                    <th className="py-3 px-4 text-left font-lato font-medium text-sm text-[#475467]">Score</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {innerDriveAssessmentRows.map((item, index) => (
                    <tr key={item.name} className="border-t border-gray-200 text-start">
                      <td className="py-3 px-4 font-lato font-medium text-sm text-[#1D2939]">{index + 1}</td>
                      <td className="py-3 px-4 font-lato font-medium text-sm text-[#1D2939]">{item.name}</td>
                      <td className="py-3 px-4 font-lato font-medium text-sm text-[#1D2939]">{String(item.score).padStart(2, '0')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {innerDriveCoachRows.length > 0 && (
              <div>
                <h4 className="font-lato font-semibold text-[14px] leading-[150%] text-[#101828] mb-3 text-start">
                  Final Top 2 Inner Drives – After Discussion With Coach:
                </h4>
                <table className="w-full border border-gray-200 rounded-lg overflow-hidden">
                  <thead>
                    <tr className="bg-[#FFF4E5]">
                      <th className="py-3 px-4 text-left font-lato font-medium text-sm text-[#475467]">Rank</th>
                      <th className="py-3 px-4 text-left font-lato font-medium text-sm text-[#475467]">Inner Drive</th>
                      <th className="py-3 px-4 text-left font-lato font-medium text-sm text-[#475467]">Score</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {innerDriveCoachRows.map((item, index) => (
                      <tr key={item.name} className="border-t border-gray-200 text-start">
                        <td className="py-3 px-4 font-lato font-medium text-sm text-[#1D2939]">{index + 1}</td>
                        <td className="py-3 px-4 font-lato font-medium text-sm text-[#1D2939]">{item.name}</td>
                        <td className="py-3 px-4 font-lato font-medium text-sm text-[#1D2939]">{String(item.score).padStart(2, '0')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-[#FFF4E5]">
                <th className="py-3 px-4 text-left font-lato font-medium text-sm text-[#475467] w-16 rounded-l-md">S No.</th>
                <th className="py-3 px-4 text-left font-lato font-medium text-sm text-[#475467]">Personality Type</th>
                <th className="py-3 px-4 text-left font-lato font-medium text-sm text-[#475467] rounded-r-md">Score</th>
              </tr>
            </thead>
            <tbody className="bg-white text-start">
              {innerDriveFallbackRows.map((item, index) => (
                <tr key={item.key} className="border-t border-gray-200 hover:bg-gray-50">
                  <td className="py-3 px-4 font-lato font-medium text-sm text-[#1D2939]">{String(index + 1).padStart(2, '0')}</td>
                  <td className="py-3 px-4 font-lato font-medium text-sm text-[#1D2939]">{item.key}</td>
                  <td className="py-3 px-4 font-lato font-medium text-sm text-[#1D2939]">{item.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}

      {/* TOOL ID 8: Human Needs */}
      {isHumanNeedsResult && humanNeedsRows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead>
              <tr className="bg-[#FFF4E5]">
                <th className="py-3 px-4 font-lato font-medium text-sm text-[#475467] w-16 rounded-l-md">
                  S No.
                </th>
                <th className="py-3 px-4 font-lato font-medium text-sm text-[#475467]">
                  Personality Type
                </th>
                <th className="py-3 px-4 font-lato font-medium text-sm text-[#475467] rounded-r-md">
                  Score
                </th>
              </tr>
            </thead>
            <tbody className="bg-white text-start">
              {humanNeedsRows.map((item, index) => (
                <tr
                  key={item.key}
                  className="border-t border-gray-200 hover:bg-gray-50"
                >
                  <td className="py-3 px-4 font-lato font-medium text-sm text-[#1D2939]">
                    {String(index + 1).padStart(2, "0")}
                  </td>
                  <td className="py-3 px-4 font-lato font-medium text-sm text-[#1D2939]">
                    {item.key}
                  </td>
                  <td className="py-3 px-4 font-lato font-medium text-sm text-[#1D2939]">
                    {item.value}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* TOOL ID 5: Baseline */}
      {isBaselineResult && (
        <div className="grid grid-cols-2 gap-6">
          {score?.selected_questions && (
            <div>
              <p className="font-lato font-semibold text-[14px] text-[#101828] mb-3 text-center">
                System Generated Baseline
              </p>
              <div className="flex flex-col gap-3">
                {score.selected_questions.map((item, index) => (
                  <div
                    key={item.id}
                    className="bg-[#FF8E1C] transition-colors duration-200 rounded-lg px-4 py-2 min-h-[42px] flex items-center justify-center text-white text-center font-medium text-sm shadow-sm"
                  >
                    {index + 1}. {item.text}
                  </div>
                ))}
              </div>
            </div>
          )}
          {score?.user_ordered_items && (
            <div>
              <p className="font-lato font-semibold text-[14px] text-[#101828] mb-3 text-center">
                User Reordered Baseline
              </p>
              <div className="flex flex-col gap-3">
                {score.user_ordered_items.map((item, index) => (
                  <div
                    key={item.id}
                    className="bg-[#FF8E1C] transition-colors duration-200 rounded-lg px-4 py-2 min-h-[42px] flex items-center justify-center text-white text-center font-medium text-sm shadow-sm"
                  >
                    {index + 1}. {item.text}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* TOOL ID 6: Personality Test */}
      {isPersonalityTestResult && (
        <div className="space-y-4 p-1">
          {personalityAssessmentType.length === 0 && personalityTestRows.length > 0 && (
            <table className="w-full">
              <thead>
                <tr className="bg-[#FFF4E5]">
                  <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">S No.</th>
                  <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">Personality Type</th>
                  <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">Your Score</th>
                </tr>
              </thead>
              <tbody className="text-start">
                {personalityTestRows.map((item, index) => (
                  <tr key={index} className="border-t border-gray-100">
                    <td className={`py-2 px-3 font-lato text-sm ${item.value === highestPersonalityScore ? 'text-orange-600 font-semibold' : 'text-[#1D2939]'}`}>
                      {String(index + 1).padStart(2, '0')}
                    </td>
                    <td className={`py-2 px-3 font-lato text-sm ${item.value === highestPersonalityScore ? 'text-orange-600 font-semibold' : 'text-[#1D2939]'}`}>
                      {item.key}
                    </td>
                    <td className={`py-2 px-3 font-lato text-sm ${item.value === highestPersonalityScore ? 'text-orange-600' : 'text-[#1D2939]'}`}>
                      {item.value}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {personalityAssessmentType.length > 0 && (
            <div>
              <h4 className="font-lato font-semibold text-[13px] leading-[150%] text-[#101828] mb-2 text-start">
                Personality Type – As Per Assessment:
              </h4>
              <table className="w-full border border-gray-200 rounded-lg overflow-hidden">
                <thead>
                  <tr className="bg-[#FFF4E5]">
                    <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">Rank</th>
                    <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">Personality Type</th>
                    <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">Score</th>
                  </tr>
                </thead>
                <tbody className="text-start">
                  {personalityAssessmentType.map((item, index) => (
                    <tr key={index} className="border-t border-gray-100">
                      <td className="py-2 px-3 font-lato text-sm text-[#1D2939]">{index + 1}</td>
                      <td className="py-2 px-3 font-lato text-sm text-[#1D2939]">{item.name}</td>
                      <td className="py-2 px-3 font-lato text-sm text-[#1D2939]">{String(item.score).padStart(2, '0')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {personalityCoachType.length > 0 && (
            <div>
              <h4 className="font-lato font-semibold text-[13px] leading-[150%] text-[#101828] mb-2 text-start">
                Personality Type – After Discussion With Coach:
              </h4>
              <table className="w-full border border-gray-200 rounded-lg overflow-hidden">
                <thead>
                  <tr className="bg-[#FFF4E5]">
                    <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">Rank</th>
                    <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">Personality Type</th>
                    <th className="py-2 px-3 text-left font-lato font-medium text-sm text-[#475467]">Score</th>
                  </tr>
                </thead>
                <tbody className="text-start">
                  {personalityCoachType.map((item, index) => (
                    <tr key={index} className="border-t border-gray-100">
                      <td className="py-2 px-3 font-lato text-sm text-[#1D2939]">{index + 1}</td>
                      <td className="py-2 px-3 font-lato text-sm text-[#1D2939]">{item.name}</td>
                      <td className="py-2 px-3 font-lato text-sm text-[#1D2939]">{String(item.score).padStart(2, '0')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* TOOL ID 7: Traits Test */}
      {isTraitsTestResult && traitsTestRows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead>
              <tr className="bg-[#FFF4E5]">
                <th className="py-3 px-4 font-lato font-medium text-sm text-[#475467] rounded-l-md">
                  S No.
                </th>
                <th className="py-3 px-4 font-lato font-medium text-sm text-[#475467]">
                  Trait
                </th>
                <th className="py-3 px-4 font-lato font-medium text-sm text-[#475467]">
                  Level
                </th>
                <th className="py-3 px-4 font-lato font-medium text-sm text-[#475467] rounded-r-md">
                  Score
                </th>
              </tr>
            </thead>
            <tbody className="bg-white text-start">
              {traitsTestRows.map((item, index) => (
                <tr
                  key={index}
                  className="border-t border-gray-200 hover:bg-gray-50"
                >
                  <td className="py-3 px-4 font-lato font-medium text-sm text-[#1D2939]">
                    {String(index + 1).padStart(2, "0")}
                  </td>
                  <td className="py-3 px-4 font-lato font-medium text-sm text-[#1D2939]">
                    {item.name}
                  </td>
                  <td className="py-3 px-4 font-lato font-medium text-sm text-[#1D2939]">
                    {capitalizeFirstLetter(item.level)}
                  </td>
                  <td className="py-3 px-4 font-lato font-medium text-sm text-[#1D2939]">
                    {item.score}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Fallback: unknown tool type but has results */}
      {!isNCodesResult &&
        !isPersonalityTestResult &&
        !isBaselineResult &&
        !isTraitsTestResult &&
        !isHumanNeedsResult &&
        !isInnerDriveResult && (
          <div className="flex items-center justify-center py-8">
            <p className="font-lato font-normal text-[14px] text-[#475467]">
              Results are available.
            </p>
          </div>
        )}
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────
const ScienceOfSelfSessionView = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalQuestions, setModalQuestions] = useState([]);
  const [modalAssignment, setModalAssignment] = useState(null);
  const dispatch = useDispatch();
  const location = useLocation();
  const loading = useSelector(selectScienceOfSelfSessionDetailsLoading);
  const sessionDetails = useSelector(selectScienceOfSelfSessionDetails);
console.log("Session Details:", sessionDetails); // Debug log for session details
  // Get IDs from URL
  const query = new URLSearchParams(location.search);
  const science_of_self_id = query.get("id");
  const science_of_self_session_id = query.get("session_id");

  useEffect(() => {
    if (science_of_self_id && science_of_self_session_id) {
      dispatch(
        getScienceOfSelfSessionDetails({
          science_of_self_id,
          science_of_self_session_id,
        })
      );
    }
  }, [dispatch, science_of_self_id, science_of_self_session_id]);

  // Open modal with questions for a specific assignment
  const handleOpenModal = (assignment) => {
    const questions = (assignment.questions || []).map((q, idx) => ({
      question: q.question_text || q.question || `Q${idx + 1}`,
      question_id: q.question_id || idx,
    }));
    setModalQuestions(questions);
    setModalAssignment(assignment);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setModalQuestions([]);
    setModalAssignment(null);
  };

  // Use participant for employee and company info
  const participant = sessionDetails?.participant;
  const employeeInfo = participant
    ? {
        name:
          [participant.first_name, participant.last_name]
            .filter(Boolean)
            .join(" ") || "-",
        mobile: participant.phone_number || "-",
        email: participant.email || "-",
      }
    : null;

  const companyInfo = participant
    ? {
        name: participant.company_name || "-",
        phone: participant.phone_landline || "-",
        address: participant.address || "-",
      }
    : null;

  const sessionInfo = {
    name:
      sessionDetails?.session_name || sessionDetails?.session_title || "-",
    date: sessionDetails?.session_date
      ? new Date(sessionDetails.session_date).toLocaleDateString()
      : "-",
    coach: sessionDetails?.coach?.first_name + " " + sessionDetails?.coach?.last_name || "-",
  };

  const documents =
    sessionDetails?.supported_documents ||
    sessionDetails?.supportedDocuments ||
    [];

  const assignments = sessionDetails?.assignments || [];

  // Tools array for assessment result
  const tools = sessionDetails?.tools || [];

  // --- Helper: Get File Icon based on extension ---
  const getFileIcon = (fileName) => {
    const ext = fileName?.toLowerCase() || "";
    const isImage = [".png", ".jpg", ".jpeg", ".gif", ".webp"].some((e) =>
      ext.includes(e)
    );
    const isPdf = ext.includes(".pdf");
    
    // Default image icon (matches your screenshot style loosely)
    if (isImage) {
      return (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path
            d="M9.46602 12.666H3.28693C2.88305 12.666 2.68111 12.666 2.5876 12.5862C2.50647 12.5169 2.46341 12.4129 2.47178 12.3065C2.48143 12.1839 2.62422 12.0411 2.90981 11.7556L8.57844 6.08693C8.84244 5.82292 8.97445 5.69091 9.12667 5.64145C9.26057 5.59795 9.4048 5.59795 9.53869 5.64145C9.69091 5.69091 9.82292 5.82292 10.0869 6.08693L12.666 8.66602V9.46602M9.46602 12.666C10.5861 12.666 11.1462 12.666 11.574 12.448C11.9503 12.2563 12.2563 11.9503 12.448 11.574C12.666 11.1462 12.666 10.5861 12.666 9.46602M9.46602 12.666H3.86602C2.74591 12.666 2.18586 12.666 1.75803 12.448C1.38171 12.2563 1.07575 11.9503 0.884003 11.574C0.666016 11.1462 0.666016 10.5861 0.666016 9.46602V3.86602C0.666016 2.74591 0.666016 2.18586 0.884003 1.75803C1.07575 1.38171 1.38171 1.07575 1.75803 0.884003C2.18586 0.666016 2.74591 0.666016 3.86602 0.666016H9.46602C10.5861 0.666016 11.1462 0.666016 11.574 0.884003C11.9503 1.07575 12.2563 1.38171 12.448 1.75803C12.666 2.18586 12.666 2.74591 12.666 3.86602V9.46602M5.66602 4.33268C5.66602 5.06906 5.06906 5.66602 4.33268 5.66602C3.5963 5.66602 2.99935 5.06906 2.99935 4.33268C2.99935 3.5963 3.5963 2.99935 4.33268 2.99935C5.06906 2.99935 5.66602 3.5963 5.66602 4.33268Z"
            stroke="white"
            strokeWidth="1.33333"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    }
    if (isPdf) {
      return (
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M11.6424 10.2861C11.4593 10.34 11.1905 10.3461 10.9023 10.3044C10.593 10.2596 10.2775 10.1652 9.96737 10.0261C10.5204 9.9458 10.9494 9.97055 11.3163 10.1003C11.4032 10.1311 11.546 10.2133 11.6424 10.2861ZM8.55703 9.77971C8.53453 9.78582 8.51237 9.7916 8.49032 9.79771C8.34152 9.83814 8.19675 9.87759 8.05723 9.91269L7.8691 9.96031C7.49068 10.0559 7.10376 10.1536 6.72161 10.2698C6.86684 9.92023 7.00177 9.56675 7.13388 9.22115C7.2317 8.96532 7.33159 8.70399 7.43489 8.44602C7.4873 8.53242 7.54199 8.61886 7.5989 8.70552C7.85817 9.09972 8.18409 9.46419 8.55703 9.77971ZM7.59443 5.83722C7.61896 6.26863 7.52572 6.68364 7.38895 7.08177C7.22047 6.58947 7.14196 6.04584 7.35259 5.60694C7.40661 5.49444 7.45086 5.43429 7.47956 5.4029C7.52388 5.47122 7.58218 5.62407 7.59443 5.83722ZM5.6188 11.302C5.52414 11.4711 5.42752 11.6293 5.32849 11.7788C5.0895 12.1383 4.69864 12.5233 4.49785 12.5233C4.47809 12.5233 4.45418 12.5201 4.41924 12.4833C4.39675 12.4597 4.39314 12.4429 4.39423 12.4198C4.401 12.2876 4.57645 12.0522 4.83062 11.8339C5.06131 11.6359 5.32206 11.4598 5.6188 11.302ZM12.2817 10.3048C12.2511 9.86446 11.5085 9.58197 11.5012 9.57935C11.2142 9.47776 10.9024 9.42841 10.548 9.42841C10.1686 9.42841 9.7596 9.4832 9.23433 9.60571C8.76694 9.27496 8.3632 8.86089 8.06155 8.40249C7.92834 8.19995 7.80859 7.99779 7.7042 7.80031C7.95904 7.19203 8.18852 6.53799 8.14682 5.80546C8.1132 5.2181 7.84788 4.82356 7.48707 4.82356C7.23955 4.82356 7.02645 5.00656 6.85316 5.36799C6.54404 6.01201 6.62529 6.83607 7.09455 7.8194C6.92555 8.21566 6.76853 8.62648 6.61654 9.02416C6.42744 9.51867 6.23259 10.0289 6.01298 10.5142C5.39712 10.7575 4.89118 11.0525 4.46953 11.4146C4.1933 11.6515 3.8603 12.0135 3.84129 12.3915C3.83201 12.5695 3.89316 12.7328 4.0174 12.8636C4.1494 13.0024 4.31523 13.0756 4.49758 13.0758C5.09982 13.0758 5.67944 12.2498 5.7894 12.0841C6.01069 11.7511 6.21783 11.3797 6.42079 10.9512C6.93197 10.7668 7.47674 10.6291 8.00475 10.4961L8.19385 10.4481C8.336 10.412 8.48374 10.3721 8.63528 10.3307C8.79564 10.2875 8.96063 10.2426 9.12832 10.1999C9.67049 10.5442 10.2535 10.7687 10.8222 10.8511C11.3011 10.9206 11.7265 10.8803 12.0144 10.7304C12.2735 10.5957 12.2878 10.3878 12.2817 10.3048ZM13.4481 14.0911C13.4481 14.8975 12.7361 14.9472 12.5925 14.9488H3.40675C2.60188 14.9488 2.55341 14.2332 2.55188 14.0911L2.55177 1.90866C2.55177 1.10146 3.26505 1.05252 3.40664 1.0509H9.61178L9.61508 1.05418V3.47149C9.61508 3.95661 9.90888 4.87509 11.0215 4.87509H13.4274L13.448 4.89567L13.4481 14.0911ZM12.8777 4.3241H11.0219C10.2173 4.3241 10.1686 3.61238 10.1675 3.47156V1.6074L12.8777 4.3241ZM14 14.0911V4.66839L10.1675 0.826633V0.808759H10.1492L9.84138 0.5H3.40677C2.92017 0.5 2 0.794501 2 1.90892V14.0913C2 14.5785 2.29412 15.5 3.40677 15.5H12.5933C13.0798 15.4999 14 15.2054 14 14.0911Z"
            fill="white"
          />
        </svg>
      );
    }
    // Default fallback icon
    return (
      <svg width="14" height="16" viewBox="0 0 14 16" fill="none">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M5.15783 0C4.96241 0 4.77499 0.0766254 4.63681 0.213011L0.215811 4.57665C0.0776328 4.71304 0 4.89802 0 5.09091V13.8182C0 15.0232 0.989677 16 2.2105 16H11.0525C12.2733 16 13.263 15.0232 13.263 13.8182V2.18182C13.263 0.976836 12.2733 0 11.0525 0H5.15783Z"
          fill="white"
        />
      </svg>
    );
  };

  // Helper: Card Loader wrapper
  const CardLoader = () => (
    <div className="flex items-center justify-center py-8">
      <ThreeDotBouncingLoader />
    </div>
  );

  // Helper for text fields
  const InfoField = ({ label, value }) => (
    <div className="flex flex-col text-start">
      <span className="block font-lato font-medium text-[14px] text-[#98A2B3]">{label}</span>
      <span className="font-lato font-medium text-[16px] leading-[150%] text-[#101828] mb-0 text-start">
        {value}
      </span>
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="font-lato font-semibold text-[28px] leading-[38px] text-[#101828]">
          {loading ? "Session Details" : sessionInfo.name || "Session Details"}
        </h1>
        {/* Dynamic Status Badge */}
        {!loading && (
          <span
            className={`px-4 py-1.5 text-sm font-medium rounded-full ${getStatusBadge(
              sessionDetails?.status
            )}`}
          >
            {sessionDetails?.status
              ? sessionDetails.status.charAt(0).toUpperCase() +
                sessionDetails.status.slice(1)
              : "-"}
          </span>
        )}
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
        {/* Employee Information */}
        <ToolManagmentCard>
          <div className="border-b border-gray-100 pb-4 mb-4 text-start">
            <h2 className="font-lato font-semibold text-[16px] text-[#101828]">
              Employee Information
            </h2>
          </div>
          {loading ? (
            <CardLoader />
          ) : employeeInfo ? (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <InfoField label="Name" value={employeeInfo.name} />
                <InfoField label="Company Name" value={companyInfo.name} />
                <InfoField label="Mobile Number" value={employeeInfo.mobile} />
                <InfoField label="Email" value={employeeInfo.email} />
                <InfoField label="Address" value={companyInfo.address} />
              </div>
            </div>
          ) : (
            <span className="text-gray-400 text-sm">
              No employee information available
            </span>
          )}
        </ToolManagmentCard>

        {/* Session Information */}
        <ToolManagmentCard>
          <div className="border-b border-gray-100 pb-4 mb-4 text-start">
            <h2 className="font-lato font-semibold text-[16px] text-[#101828]">
              Session Information
            </h2>
          </div>
          {loading ? (
            <CardLoader />
          ) : (
            <div className="grid grid-cols-2 gap-y-6 gap-x-4">
              <InfoField label="Session Name" value={sessionInfo.name} />
              <InfoField label="Session Date" value={sessionInfo.date} />
              <InfoField label="Coach Name" value={sessionInfo.coach} />
            </div>
          )}
        </ToolManagmentCard>

        {/* ─── Supported Documents (Updated) ─── */}
        <ToolManagmentCard>
          <div className="border-b border-gray-100 pb-4 mb-4 text-start">
            <h2 className="font-lato font-semibold text-[16px] text-[#101828]">
              Supported Documents
            </h2>
          </div>
          {loading ? (
            <CardLoader />
          ) : documents.length === 0 ? (
            <span className="text-gray-400 text-sm">
              No documents available
            </span>
          ) : (
            <div className="flex flex-col gap-5">
              {documents.map((group, groupIndex) => (
                <div key={groupIndex} className="flex flex-col gap-2">
                  {/* Document Group Title (e.g. "Test_Document") */}
                  <h3 className="font-lato font-medium text-[14px] text-[#101828] text-start">
                    {group.name}
                  </h3>
                  
                  {/* Grid of actual files inside the group */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {group.documents?.map((doc, docIndex) => {
                      const fileName = getFileNameFromUrl(doc.url);
                      return (
                        <div
                          key={docIndex}
                          onClick={() => {
                            if (doc.url) {
                              window.open(doc.url, "_blank", "noopener,noreferrer");
                            }
                          }}
                          className="flex items-center gap-3 p-2 border border-gray-200 rounded-lg hover:border-[#FF8E1C] hover:bg-orange-50 transition-all cursor-pointer bg-white"
                        >
                          <div className="relative w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                            {/* Lighter orange halo background matching the image */}
                            <div className="absolute inset-0 rounded-full bg-[#FF8E1C] opacity-20" />
                            {/* Inner orange circle with icon */}
                            <div className="relative w-8 h-8 rounded-full bg-[#FF8E1C] flex items-center justify-center text-white">
                              {getFileIcon(fileName)}
                            </div>
                          </div>
                          
                          {/* File Name with truncation and tooltip */}
                          <span 
                            className="flex-1 text-[13px] text-[#475467] truncate text-start"
                            title={fileName}
                          >
                            {fileName}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ToolManagmentCard>

        {/* Assignments */}
        <ToolManagmentCard>
          <div className="pb-4 text-start">
            <h2 className="font-lato font-semibold text-[16px] text-[#101828]">
              Assignments
            </h2>
          </div>
          {loading ? (
            <CardLoader />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-[#FFF4E5] text-[#475467]">
                  <tr>
                    <th className="py-3 px-4 font-medium w-16 rounded-l-md">
                      S No.
                    </th>
                    <th className="py-3 px-4 font-medium">Assignment Name</th>
                    <th className="py-3 px-4 font-medium text-center rounded-r-md">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {assignments.length === 0 ? (
                    <tr>
                      <td
                        colSpan={3}
                        className="text-center text-gray-400 py-4"
                      >
                        No assignments
                      </td>
                    </tr>
                  ) : (
                    assignments.map((item, index) => (
                      <tr key={index}>
                        <td className="py-4 px-4 text-[#1D2939]">{`0${
                          index + 1
                        }`}</td>
                        <td className="py-4 px-4 text-[#1D2939]">
                          {item.assignment_name || item.name || "-"}
                        </td>
                        <td className="py-4 px-4 text-center">
                          <button
                            onClick={() => handleOpenModal(item)}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            <Eye size={18} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </ToolManagmentCard>

        {/* ─── Assessment Result (Tools) – Dynamic per tool_id ─── */}
        <ToolManagmentCard>
          <div className="pb-4 text-start">
            <h2 className="font-lato font-semibold text-[16px] text-[#101828]">
              Assessment Result
            </h2>
          </div>
          <div className="border-b border-gray-200 mb-3"></div>
          {loading ? (
            <CardLoader />
          ) : tools.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <p className="font-lato font-normal text-[14px] text-[#475467]">
                No assessment data
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {tools.map((tool, idx) => (
                <ToolResultSection key={tool.tool_id || idx} tool={tool} />
              ))}
            </div>
          )}
        </ToolManagmentCard>
      </div>

      {/* Dialogs / Modals */}
      <AddEditQuestionAnswer
        dialogOpen={isModalOpen}
        onDialogClose={handleCloseModal}
        questionsData={modalQuestions}
        isViewMode={true}
        sessionData={modalAssignment}
      />
    </div>
  );
};

export default ScienceOfSelfSessionView;
